import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { handleFeatureNotIncluded } from "../../utils/notifyPlanUpgrade";
import { ButtonBack, ButtonSave } from "../../components/ui";
import api from "../../services/api";
import styles from "./Blocks.module.css";

const formatDate = (isoString) => {
  const date = new Date(isoString.split("T")[0] + "T12:00:00");
  return date.toLocaleDateString("pt-BR", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function Blocks() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [removing, setRemoving] = useState(null);

  // Form state
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Conflict warning state
  const [conflict, setConflict] = useState(null); // { appointments: [] }

  const fetchBlocks = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/blocks");
      setBlocks(res.data.blocks ?? res.data);
    } catch {
      toast.error("Erro ao carregar bloqueios.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const resetForm = () => {
    setDate("");
    setReason("");
    setConflict(null);
    setShowForm(false);
  };

  const handleCreate = async (forceBlock = false) => {
    if (!date) {
      toast.error("Informe a data do bloqueio.");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/blocks", { date, reason: reason || undefined, forceBlock });

      if (res.data?.warning) {
        // Has conflict — show warning panel
        setConflict({ appointments: res.data.appointments ?? [] });
        setSaving(false);
        return;
      }

      toast.success("Bloqueio cadastrado com sucesso!");
      resetForm();
      fetchBlocks();
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca erro cru nem a copy genérica.
      if (handleFeatureNotIncluded(err)) return;
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("Já existe um bloqueio para esta data.");
      } else {
        toast.error("Erro ao criar bloqueio.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("Deseja remover este bloqueio?")) return;
    setRemoving(id);
    try {
      await api.delete(`/blocks/${id}`);
      toast.success("Bloqueio removido.");
      setBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      // 403 FEATURE_NOT_INCLUDED (Plano Atendimento — ADR-0026): mensagem
      // humana com CTA, nunca erro cru nem a copy genérica.
      if (!handleFeatureNotIncluded(err)) {
        toast.error("Erro ao remover bloqueio.");
      }
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <ButtonBack to="/agenda" />
        <div className={styles.headerCenter}>
          <h1 className={styles.title}>Bloqueios de Agenda</h1>
          <p className={styles.subtitle}>Gerencie os dias em que sua agenda estará indisponível</p>
        </div>
        {!showForm && (
          <button className={styles.newBtn} onClick={() => setShowForm(true)}>
            ＋ Novo Bloqueio
          </button>
        )}
      </div>

      {/* Inline Form */}
      {showForm && (
        <div className={styles.formCard}>
          <h2 className={styles.formTitle}>Novo Bloqueio</h2>

          {/* Conflict Warning */}
          {conflict && (
            <div className={styles.conflictWarning}>
              <p className={styles.conflictTitle}>
                ⚠️ Existem {conflict.appointments.length} agendamento(s) neste dia. Deseja bloquear mesmo assim?
              </p>
              {conflict.appointments.length > 0 && (
                <ul className={styles.conflictList}>
                  {conflict.appointments.map((app) => (
                    <li key={app.id} className={styles.conflictItem}>
                      <span className={styles.conflictTime}>
                        {app.startTime ? app.startTime.split("T")[1]?.substring(0, 5) : "—"}
                      </span>
                      {app.clientName && <span className={styles.conflictName}>{app.clientName}</span>}
                    </li>
                  ))}
                </ul>
              )}
              <div className={styles.conflictActions}>
                <ButtonSave
                  onClick={() => handleCreate(true)}
                  isLoading={saving}
                  loadingText="Bloqueando..."
                >
                  Sim, bloquear mesmo assim
                </ButtonSave>
                <ButtonBack onClick={resetForm} label="Cancelar" />
              </div>
            </div>
          )}

          {/* Form Fields — hidden during conflict to avoid double-submit */}
          {!conflict && (
            <>
              <div className={styles.formRow}>
                <label className={styles.fieldLabel}>
                  Data
                  <input
                    type="date"
                    className={styles.input}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                  />
                </label>

                <label className={styles.fieldLabel}>
                  Motivo <span className={styles.optional}>(opcional)</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Ex.: Feriado, Férias..."
                    maxLength={200}
                  />
                </label>
              </div>

              <div className={styles.formActions}>
                <ButtonSave
                  onClick={() => handleCreate(false)}
                  isLoading={saving}
                  loadingText="Bloqueando..."
                >
                  Bloquear
                </ButtonSave>
                <ButtonBack onClick={resetForm} label="Cancelar" />
              </div>
            </>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className={styles.loading}>Carregando bloqueios...</div>
      ) : blocks.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>🔓</span>
          <p>Nenhum bloqueio cadastrado.</p>
          <p className={styles.emptyHint}>Clique em <strong>＋ Novo Bloqueio</strong> para adicionar um dia indisponível.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {blocks.map((block) => (
            <div key={block.id} className={styles.blockCard}>
              <div className={styles.blockIcon}>🔒</div>
              <div className={styles.blockInfo}>
                <p className={styles.blockDate}>{formatDate(block.date)}</p>
                <p className={styles.blockReason}>
                  {block.reason || <em className={styles.noReason}>Sem motivo informado</em>}
                </p>
              </div>
              <button
                className={styles.removeBtn}
                onClick={() => handleRemove(block.id)}
                disabled={removing === block.id}
                aria-label="Remover bloqueio"
              >
                {removing === block.id ? "Removendo..." : "Remover"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
